import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY } from "../../lib/format";

const STATUS_STYLE = {
  SUCCESS: "bg-emerald-500/15 text-emerald-400",
  FAILED: "bg-rose-500/15 text-rose-400",
  VOID: "bg-neutral-500/15 text-neutral-300",
  VOID_FAILED: "bg-rose-500/15 text-rose-400",
  REFUND: "bg-amber-500/15 text-amber-400",
  PARTIAL_REFUND: "bg-amber-500/15 text-amber-400",
};
const STATUS_LABEL = {
  SUCCESS: "Başarılı", FAILED: "Başarısız", VOID: "İptal", VOID_FAILED: "İptal Başarısız",
  REFUND: "İade", PARTIAL_REFUND: "Kısmi İade",
};

export default function PaymentLogsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: "30" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/admin/payment-logs?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [page, q, status]);
  useEffect(load, [load]);

  return (
    <div className="space-y-5" data-testid="payment-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-xl text-white">Ödeme Logları</h1>
          <p className="text-xs text-neutral-500 mt-1">Banka/sağlayıcı yanıtları — hangi ödemenin neden başarısız olduğunu buradan görürsünüz.</p>
        </div>
        <button onClick={load} className="h-9 px-4 rounded-lg border border-[#272B33] text-xs text-neutral-300 hover:bg-[#1E222A] flex items-center gap-2" data-testid="payment-logs-refresh">
          <RefreshCw size={13} /> Yenile
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Sipariş no ara (BIG-...)"
            className="h-10 pl-9 pr-3 w-64 bg-[#0F1115] border border-[#272B33] rounded-lg text-sm text-neutral-200 focus:outline-none focus:border-blue-500"
            data-testid="payment-logs-search" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="h-10 px-3 bg-[#0F1115] border border-[#272B33] rounded-lg text-sm text-neutral-200" data-testid="payment-logs-status-filter">
          <option value="">Tüm Durumlar</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
              <th className="px-4 py-3">Tarih</th>
              <th className="px-4 py-3">Sipariş No</th>
              <th className="px-4 py-3">Müşteri</th>
              <th className="px-4 py-3">Sağlayıcı</th>
              <th className="px-4 py-3">Tutar</th>
              <th className="px-4 py-3">Durum</th>
              <th className="px-4 py-3">Banka Yanıtı</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((t) => (
              <tr key={t.payment_transaction_id} className="border-b border-[#272B33]/60 hover:bg-[#1E222A]/50" data-testid={`payment-log-row-${t.order_number}`}>
                <td className="px-4 py-3 text-neutral-400 text-xs whitespace-nowrap">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 font-mono-ed text-white text-xs">{t.order_number}</td>
                <td className="px-4 py-3 text-xs text-neutral-400">{t.customer_name || "—"}<br /><span className="text-neutral-600">{t.customer_email || ""}</span></td>
                <td className="px-4 py-3 text-xs text-neutral-300">{t.provider}{t.installment_count > 1 ? ` · ${t.installment_count} taksit` : ""}</td>
                <td className="px-4 py-3 font-mono-ed text-white text-xs">{fmtTRY(t.amount)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[t.status] || "bg-neutral-500/15 text-neutral-300"}`} data-testid={`payment-log-status-${t.order_number}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-400 max-w-[280px]">
                  {t.response_code && <span className="font-mono-ed text-neutral-500">kod:{t.response_code} </span>}
                  {t.md_status && <span className="font-mono-ed text-neutral-500">md:{t.md_status} </span>}
                  <span className="text-neutral-300">{t.response_message || t.md_error || "—"}</span>
                  {t.masked_card && <div className="text-neutral-600 font-mono-ed mt-0.5">{t.masked_card}</div>}
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500 text-sm">Kayıt bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.total > data?.limit && (
        <div className="flex gap-2 justify-center">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 px-4 rounded-lg border border-[#272B33] text-xs text-neutral-300 disabled:opacity-40" data-testid="payment-logs-prev">Önceki</button>
          <span className="text-xs text-neutral-500 self-center">Sayfa {data.page} / {Math.ceil(data.total / data.limit)}</span>
          <button disabled={page * data.limit >= data.total} onClick={() => setPage(page + 1)} className="h-9 px-4 rounded-lg border border-[#272B33] text-xs text-neutral-300 disabled:opacity-40" data-testid="payment-logs-next">Sonraki</button>
        </div>
      )}

      <p className="text-[11px] text-neutral-600 flex items-center gap-1.5">
        <ShieldCheck size={12} /> Kart numarası, CVV ve API anahtarları güvenlik gereği hiçbir zaman bu ekranda gösterilmez ve loglanmaz.
      </p>
    </div>
  );
}
